import React, { PureComponent, Fragment } from "react";
import moment from "moment";
import clsx from "clsx";

import {
  Bar,
  Tooltip,
  ResponsiveContainer,
  YAxis,
  XAxis,
  CartesianGrid,
  Cell,
  ReferenceDot,
  Line,
  ComposedChart,
} from "recharts";

import missing_image from "./../../i/missing_image.svg";

import {
  MetricTable,
  selectFormat,
  MAIN_COLORS,
  CustomizedYAxisTick,
  StandardTooltip,
  Error,
  truncate,
  formatDateFromData,
  isDateFromDataInISOFormat,
  getImageSrc,
  getAllKeys,
  renderPhases,
  getPhaseWidths,
  renderPhaseTitles,
  getSpecifiedLegendOrder,
} from "components/Utils/Utils.js";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  RenderBackground,
  InsightsTickIndicator,
  closeAllBarchartTooltips,
} from "components/Utils/InsightsUtils.js";

import { XRayTooltip } from "components/Utils/XrayUtils.js";

import { getXrayTooltipPosition, findParentNodeByClass } from "helpers/DOM";

const metricBar = (cell) => (
  <MetricTable {...cell} graph={cell.trends} graphType="bar" />
);

class WorldCupStackedBar extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      widgetsData: props.data.data || props.data.chart,
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };

    this.lineComparisonData = [
      "2018 Minutes Watched",
      "2018 Video Starts",

      "2019 Minutes Watched",
      "2019 Video Starts",
    ];
    this.node = React.createRef();
    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
    this.colorKeys = this.getSortedKeys(this.state.widgetsData).reduce(
      (prev, next, i) => ({
        ...prev,
        [next]: this.lineComparisonData.includes(next)
          ? "#65757d"
          : MAIN_COLORS[i],
      }),
      {}
    );
  }

  updatePosition() {
    // close all tooltips while resizing the screen
    closeAllBarchartTooltips();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  renderBars = (data) => {
    let bars = [];
    let labels = [];
    const sortedKeys = this.getSortedKeys(data);
    const insights = this.state.widgetsInsights;
    const isFixedBarWidth = data.length <= 7;

    [...new Set(sortedKeys)]
      .filter((bar) => !this.lineComparisonData.includes(bar))
      .forEach((bar, i) => {
        bars.push(
          <Bar
            key={i}
            dataKey={bar}
            stackId={"stack"}
            barSize={isFixedBarWidth ? 35 : null}
            fill={this.colorKeys[bar]}
            isAnimationActive={false}
            // only first bar needs background
            cursor="pointer"
            background={
              i === 0 ? (
                <RenderBackground
                  insights={insights}
                  showIndicator={!this.state.tooltip.show}
                  node={this.node}
                  i={i}
                />
              ) : null
            }
            onClick={(d) => {
              const label = labels.filter((l) => l.date === d.date);
              const extra = sortedKeys
                .map((k, j) => ({
                  key: k,
                  value: d[k],
                  color: this.colorKeys[k],
                }))
                .filter((o) => o.value !== undefined);

              d.extra = extra;
              d.insight = label.length ? label[0] : null;

              d.posts = d.posts || []; // TODO: get correct data from api
              d.topLists = this.props.data.topLists;

              this.clickBar(d, bar);
            }}
          >
            {data.map(
              (entry, index) =>
                entry && (
                  <Cell
                    key={`cell-${bar}-${index}`}
                    fillOpacity={entry[bar] < 0 ? 0.5 : 1}
                  ></Cell>
                )
            )}
          </Bar>
        );
      });

    return bars;
  };

  clickBar(d, dataKey) {
    if (this.state.tooltip.show && d.date === this.state.tooltip.date) {
      this.setState({
        tooltip: { ...d, show: false, dataKey },
      });
    } else {
      // close all tooltips from this and other widgets as well
      closeAllBarchartTooltips();
      this.setState({
        tooltip: { ...d, show: true, dataKey },
      });
    }
  }

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    // close all tooltips when the user clicks outside the context
    if (
      !e.target.classList.contains("recharts-rectangle") &&
      // and the parent isn't a xray tooltip
      !findParentNodeByClass(e.target, "xray-tooltip-container")
    )
      closeAllBarchartTooltips();
  };

  legendElement = (data) =>
    data.map((entry, i) => (
      <span key={i} className="legend mt-2 mt-md-0">
        <span
          className="legend__icon"
          style={{
            background: this.colorKeys[entry],
          }}
        ></span>
        <span
          className="legend__text"
          style={{
            color: this.colorKeys[entry],
          }}
        >
          {entry}
        </span>
      </span>
    ));

  renderLegends = (data) => {
    return this.legendElement([
      ...new Set(
        getSpecifiedLegendOrder(this.getSortedKeys(data), [
          "2018 Minutes Watched",
          "2018 Video Starts",

          "2019 Minutes Watched",
          "2019 Video Starts",
        ])
      ),
    ]);
  };

  getSortedKeys(_data) {
    if (!_data) return [];
    if (!_data[0]) return [];
    const data = _data.filter((d) => d);
    const keys = getAllKeys(data, ["date", "topContent", "total", "full_date"]);

    const len = data.length;

    return keys
      .map((key) => ({
        key: key,
        value: data.reduce((accum, curr) => accum + (curr[key] || 0), 0) / len,
      }))
      .sort((a, b) => b.value - a.value)
      .map((d) => d.key);
  }

  getExportData = () => {
    let data = this.props.data.data || this.props.data.chart;
    return {
      data: data,
      opts: {
        type: "chart",
        date: {
          // overwrites the default settings for date only when the date is in ISO format
          defined: isDateFromDataInISOFormat(this.props.data.data),
          format: "YYYY-MM-DDThh:mm:ssZ",
          viewFormat: "YYYY-MM-DD",
        },
      },
    };
  };

  renderLines = (shouldDisplayLines) => {
    return (
      shouldDisplayLines && [
        <Line
          key="2018 Minutes Watched"
          type="monotone"
          dataKey="2018 Minutes Watched"
          stroke={this.colorKeys["2018 Minutes Watched"]}
        />,
        <Line
          key="2018 Video Starts"
          type="monotone"
          dataKey="2018 Video Starts"
          stroke={this.colorKeys["2018 Video Starts"]}
        />,
        <Line
          key="2019 Minutes Watched"
          type="monotone"
          dataKey="2019 Minutes Watched"
          stroke={this.colorKeys["2019 Minutes Watched"]}
        />,
        <Line
          key="2019 Video Starts"
          type="monotone"
          dataKey="2019 Video Starts"
          stroke={this.colorKeys["2019 Video Starts"]}
        />,
      ]
    );
  };

  render() {
    const { data, title, url } = this.props;

    const { widgetsInsights } = this.state;

    const widget_data = data.data || data.chart;
    const phases = data.phases;

    const insightsData = widgetsInsights.length > 0;

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    let preparedData = formatDateFromData(widget_data);

    const phaseWidths = getPhaseWidths(widget_data, phases);

    const shouldDisplayLines =
      [
        "/dashboard/telemundo/world-cup-daily?type=minutes-by-device&version=dev&t=world-cup-2022&campaign=world-cup-2022",
        "/dashboard/telemundo/world-cup-daily-min-watched?t=world-cup-2022&campaign=world-cup-2022",
        "/dashboard/telemundo/world-cup-daily?type=views-by-type&t=world-cup-2022&campaign=world-cup-2022",
        "/dashboard/telemundo/world-cup-daily?type=minutes-by-device&version=dev&t=world-cup-2023&campaign=world-cup-2023",
        "/dashboard/telemundo/world-cup-daily-min-watched?t=world-cup-2023&campaign=world-cup-2023",
        "/dashboard/telemundo/world-cup-daily?type=views-by-type&t=world-cup-2023&campaign=world-cup-2023",
        "/dashboard/telemundo/world-cup-daily?type=views-by-type&version=dev&t=world-cup-2023&campaign=world-cup-2023",
      ].indexOf(url) >= 0;

    return (
      <Fragment>
        <div ref={this.divPosition}>
          {this.renderLegends(preparedData)}

          <div className="composed-chart position-relative mt-3">
            {renderPhases(phaseWidths)}
            <ResponsiveContainer height={350} width="100%">
              <ComposedChart
                data={preparedData}
                margin={{ top: 12, right: 0, bottom: 0, left: -38 }}
                stackOffset="sign"
                onMouseOver={(e) => CloseSelectedInsight(this.node)}
                onMouseOut={(e) =>
                  OpenSelectedInsight(this.node, !this.state.tooltip.show)
                }
              >
                <CartesianGrid
                  stroke="#FEE5DE"
                  strokeDasharray="3 3"
                  vertical={false}
                />

                {/* Adds insight icon under x-axis ticks  */}
                {insightsData &&
                  widgetsInsights.map((o, index) => (
                    <ReferenceDot
                      x={o.date}
                      y={0}
                      key={title + index}
                      ifOverflow="extendDomain"
                      shape={
                        <InsightsTickIndicator
                          insights={widgetsInsights}
                          date={o.date}
                          node={this.node}
                        />
                      }
                    />
                  ))}

                {!this.state.tooltip.show && (
                  <Tooltip
                    cursor={{ fill: "#eee", opacity: 0.5 }}
                    content={
                      <StandardTooltip
                        render={({ data }) => {
                          const payload = data(this.props);

                          if (payload !== null) {
                            const tooltip_data = payload[0].payload;

                            let total = "";

                            let totalLine = undefined;
                            let totalLineValue = 0;
                            let totalLineLabel = "";

                            if (tooltip_data.total) {
                              total = tooltip_data.total;
                            } else {
                              total = payload
                                .filter(
                                  (el) =>
                                    !this.lineComparisonData.includes(
                                      el.dataKey
                                    )
                                )
                                .reduce(
                                  (acc, cur) => acc + (cur.value || 0),
                                  0
                                );

                              totalLine = payload.find((el) =>
                                this.lineComparisonData.includes(el.dataKey)
                              );

                              totalLineValue = totalLine?.value;

                              totalLineLabel = totalLine?.dataKey;
                            }

                            const topContent = tooltip_data.topContent;
                            let IMAGE_TOP_CONTENT = {
                              backgroundImage: `url(${missing_image})`,
                            };

                            if (topContent) {
                              if (topContent.thumbnail)
                                IMAGE_TOP_CONTENT = {
                                  backgroundImage: `url(${getImageSrc(
                                    topContent.thumbnail
                                  )})`,
                                };

                              topContent.value = topContent.views;
                              topContent.trends.forEach((chartData) => {
                                chartData.value = chartData.views;
                                chartData.label = chartData.date;
                                // eslint-disable-next-line no-self-assign
                                chartData.selected = chartData.selected;
                              });
                            }

                            const topList =
                              this.props.data.topLists &&
                              this.props.data.topLists.find(
                                (d) =>
                                  d.date.split("-").slice(1).join("/") ===
                                    tooltip_data.date ||
                                  moment(d.date).format("MM/YYYY") ===
                                    tooltip_data.date
                              ).topList;

                            if (topList) {
                              if (topList.thumbnail)
                                IMAGE_TOP_CONTENT = {
                                  backgroundImage: `url(${getImageSrc(
                                    topList.thumbnail
                                  )})`,
                                };

                              topList.value = topList.total;
                            }

                            return (
                              <div className="standard-tooltip">
                                {tooltip_data.date && (
                                  <header className="standard-tooltip__header">
                                    <span className="standard-tooltip__key-header">
                                      Date:
                                    </span>
                                    <span className="standard-tooltip__value-header">
                                      {tooltip_data.date}
                                    </span>
                                  </header>
                                )}
                                <main className="standard-tooltip__main">
                                  {Object.keys(payload)
                                    .filter(
                                      (key) =>
                                        !this.lineComparisonData.includes(
                                          payload[key].dataKey
                                        )
                                    )
                                    .sort(
                                      (a, b) =>
                                        payload[b].value - payload[a].value
                                    )
                                    .map((key, index) => {
                                      return (
                                        <div
                                          className="standard-tooltip__item"
                                          key={key}
                                        >
                                          {payload[key].dataKey !==
                                          "insights" ? (
                                            <>
                                              <span
                                                className="standard-tooltip__icon"
                                                style={{
                                                  background:
                                                    payload[key].color,
                                                }}
                                              ></span>
                                              <span
                                                className={clsx(
                                                  "standard-tooltip__key",
                                                  "text-capitalize"
                                                )}
                                                style={{
                                                  color: payload[key].color,
                                                }}
                                              >
                                                {payload[key].dataKey}{" "}
                                              </span>

                                              <span className="standard-tooltip__value">
                                                {selectFormat(
                                                  "number",
                                                  payload[key].value
                                                )}
                                              </span>
                                            </>
                                          ) : null}
                                        </div>
                                      );
                                    })}

                                  <div className="standard-tooltip__dilimiter1"></div>

                                  <div className="standard-tooltip__item">
                                    <span
                                      className={clsx(
                                        "standard-tooltip__key",
                                        "text-capitalize",
                                        "total"
                                      )}
                                    >
                                      Total{" "}
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat("number", total)}
                                    </span>
                                  </div>

                                  <div className="standard-tooltip__dilimiter1"></div>

                                  <div className="standard-tooltip__item">
                                    <span
                                      className={clsx(
                                        "standard-tooltip__key",
                                        "text-capitalize",
                                        "total"
                                      )}
                                    >
                                      {totalLineLabel}
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat("number", totalLineValue)}
                                    </span>
                                  </div>

                                  {widgetsInsights.length > 0 && (
                                    <InsightsWithinTooltip
                                      data={widgetsInsights}
                                      tooltipDate={tooltip_data.date}
                                    />
                                  )}

                                  {topContent && (
                                    <>
                                      <div className="standard-tooltip__dilimiter2 mt-3" />
                                      <div className="standard-tooltip__section-header my-2">
                                        Top content on this day.
                                      </div>
                                      <div className="standard-tooltip__details--posts">
                                        <article className="content-table">
                                          <figure className="content-table__figure">
                                            <div
                                              className="content-table__img"
                                              style={IMAGE_TOP_CONTENT}
                                            />
                                          </figure>

                                          <section className="content-table__detail">
                                            <span className="standard-tooltip__value">
                                              {metricBar(topContent)}
                                            </span>
                                          </section>
                                        </article>

                                        <div className="standard-tooltip__text">
                                          {truncate(topContent.title, 200)}
                                        </div>
                                      </div>
                                    </>
                                  )}

                                  {topList && (
                                    <>
                                      <div className="standard-tooltip__dilimiter2 mt-3" />

                                      <div className="standard-tooltip__section-header mb-3 mt-3">
                                        Top 5
                                      </div>
                                      {topList.map((item, i) => {
                                        return (
                                          <div
                                            key={i}
                                            className="standard-tooltip__details--posts mb-2"
                                          >
                                            <article className="content-table">
                                              <figure className="content-table__figure">
                                                <div
                                                  className="content-table__img"
                                                  style={{
                                                    backgroundImage: `url(${getImageSrc(
                                                      item.thumbnail
                                                    )})`,
                                                  }}
                                                />
                                              </figure>

                                              <section className="content-table__detail">
                                                <span className="standard-tooltip__value">
                                                  {metricBar(item)}
                                                </span>
                                              </section>
                                            </article>

                                            <div className="standard-tooltip__text">
                                              {truncate(item.title, 200)}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </>
                                  )}
                                </main>
                              </div>
                            );
                          }
                        }}
                      />
                    }
                  />
                )}
                {this.renderBars(preparedData)}
                {this.renderLines(shouldDisplayLines)}

                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  scale="auto"
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(d) => selectFormat("number", d)}
                  tick={<CustomizedYAxisTick />}
                />
              </ComposedChart>
            </ResponsiveContainer>
            <div className="featured-insight-tooltip" ref={this.node}>
              {widgetsInsights.length > 0 && !this.state.tooltip.show && (
                <RenderTooltipInsights data={widgetsInsights} />
              )}
            </div>
          </div>
          {renderPhaseTitles(phaseWidths)}

          <div
            className="xray-tooltip-container"
            style={{
              top: "5.4rem", //(this.state.tooltip.y - 20) + 'px',
              left:
                this.state.tooltip.x + this.state.tooltip.width + 220 <
                this.state.width
                  ? this.state.tooltip.x + this.state.tooltip.width + 15 + "px"
                  : this.state.tooltip.x -
                    getXrayTooltipPosition(
                      this.divPosition.current,
                      this.state.tooltip
                    ) +
                    "px",
              opacity: this.state.tooltip.show ? 1.0 : 0.0,
              visibility: this.state.tooltip.show ? "visible" : "hidden",
            }}
          >
            <XRayTooltip
              data={this.state.tooltip}
              insights={widgetsInsights}
              close={this.closeTooltip.bind(this)}
            />
          </div>
        </div>
      </Fragment>
    );
  }
}
export default WorldCupStackedBar;
